/**
 * LOS LOGROS DE UNA PERSONA, dibujados.
 *
 * QUÉ PROBLEMA RESUELVE. El perfil decía cuántos puntos lleva y nada más, y un
 * número suelto no cuenta una historia: 240 puntos pueden ser un torneo ganado
 * o veinte retos parejos, y son dos jugadores distintos.
 *
 * ESTA PANTALLA NO DECIDE NADA. Qué cuenta como logro, cuántos van juntos, en
 * qué orden y cuándo vencen lo resolvió el servidor: acá solo se dibuja lo que
 * llegó. Repetir cualquiera de esas reglas de este lado sería tener dos
 * versiones de la misma —y la del navegador queda vieja cada vez que alguien
 * no actualiza la pestaña—.
 *
 * CON QUÉ SE DIBUJA CADA UNO SALE DEL CONTRATO y no de un `if` acá: Javier lo
 * dijo como una regla del producto —«si son retos sale como dos raquetas si es
 * por torneo son como una copa»—, y una copa al lado de unos retos ganados
 * sería un perfil que miente.
 *
 * CADA UNO DICE QUÉ ES EN PALABRAS, no solo en dibujo. Un icono sin texto es
 * una adivinanza para quien lo ve por primera vez, y para un lector de
 * pantalla es nada.
 */
import { useTranslation } from 'react-i18next';
import { ACHIEVEMENT_ICONS, divisionOf, type Achievement } from '@tennisup/contracts';

import {
  ChallengeIcon,
  CrownIcon,
  DivisionIcon,
  MedalIcon,
  RankPositionIcon,
  TrophyIcon,
} from './icons';

/**
 * El dibujo de cada clase. El contrato dice CUÁL va; esto dice cómo se ve.
 *
 * Está escrito sobre las tres clases de dibujo y no sobre los tipos de logro,
 * así que agregar un logro nuevo no toca este archivo: el contrato ya lo
 * obligó a elegir uno de estos tres.
 */
const DIBUJOS = {
  TROPHY: TrophyIcon,
  RACKETS: ChallengeIcon,
  CROWN: CrownIcon,
  // LA PLATA Y EL BRONCE son la misma medalla con otro número adentro: el
  // dibujo es uno solo y lo que cambia es el puesto, igual que en la vida.
  SILVER: (props: { title: string; size?: number }) => <MedalIcon {...props} place={2} />,
  BRONZE: (props: { title: string; size?: number }) => <MedalIcon {...props} place={3} />,
  RANK: RankPositionIcon,
} as const;

export function Achievements({ achievements }: { achievements: Achievement[] }): JSX.Element | null {
  const { t } = useTranslation();

  // `Achievement[]` es una promesa sobre el contrato, no una garantía sobre el
  // cable: un servidor una versión atrás no manda este campo.
  const filas = Array.isArray(achievements) ? achievements : [];
  // SIN LOGROS NO SE DIBUJA NADA, ni un título ni un «todavía no tenés».
  // Quien acaba de entrar no necesita que le recuerden que no ganó nada.
  if (filas.length === 0) return null;

  return (
    <section className="achievements">
      <h2>{t('achievements.title')}</h2>
      <ul className="achievement-list">
        {filas.map((logro) => {
          const Dibujo = DIBUJOS[ACHIEVEMENT_ICONS[logro.kind]] ?? TrophyIcon;
          const nombre = t(`achievements.kind.${logro.kind}`);
          const categoria = t(`categoryLevel.${logro.level}`);
          return (
            <li
              key={`${logro.kind}|${logro.level}|${logro.communityId ?? ''}|${logro.year}`}
              className="achievement"
            >
              <Dibujo title="" size={22} />
              <span className="achievement-name">
                {nombre}
                {/* EL PUESTO, SOLO EN EL CIERRE DE AÑO. «Cerró el año 2.º» se
                    lee de corrido; «Cerró el año» a secas no dice nada. */}
                {logro.position !== null && (
                  <strong className="achievement-count">
                    {' '}
                    {/*
                      «3.º de 12» CUANDO SE SABE DE CUÁNTOS, y «3.º» a secas
                      cuando no. Ser tercero en una tabla de cuatro no es lo
                      mismo que en una de cuarenta, y el puesto solo se lee mal
                      en las dos direcciones. El cierre de año no trae `outOf`
                      —es un número viejo y la tabla de entonces ya no está— y
                      ahí sigue diciendo el puesto solo.
                    */}
                    {logro.outOf === null
                      ? t('achievements.place', { position: logro.position })
                      : t('achievements.placeOf', {
                          position: logro.position,
                          total: logro.outOf,
                        })}
                  </strong>
                )}
                {/*
                  LOS TIE BREAKS SE CUENTAN «7 de 9» Y NO «×7».
                  Javier: «cuantos tie break pierde y gana». Ganar 7 de 9 dice
                  algo muy distinto de ganar 7 de 30, y el «×7» solo cuenta la
                  mitad buena. Por eso este caso va antes que el «×N» de abajo.
                */}
                {logro.lost !== null ? (
                  <strong className="achievement-count">
                    {' '}
                    {t('achievements.tieBreaksOf', {
                      won: logro.count,
                      total: logro.count + logro.lost,
                    })}
                  </strong>
                ) : (
                  /* EL «×3» SOLO CUANDO HAY MÁS DE UNO. Un «×1» al lado de cada
                     icono es ruido en todos los perfiles para avisar de nada. */
                  logro.count > 1 && <strong className="achievement-count"> ×{logro.count}</strong>
                )}

                {/*
                  DE QUÉ CLUB Y DE QUÉ AÑO, DEBAJO DEL NOMBRE. Javier: «Poncho
                  sale primero del ranking pero no se sabe de que club, por
                  favor agregar club y también si gana torneos ponerle el año».

                  DEBAJO Y NO AL LADO: la fila ya tiene el dibujo, el nombre y
                  la categoría, y a 320 px un cuarto dato en la misma línea
                  parte el nombre del club en pedazos. Debajo del nombre se lee
                  como lo que es —la letra chica de ese logro— y no compite con
                  la categoría, que es lo que se busca al comparar dos filas.

                  EN UN SOLO RENGLÓN los dos datos: contestan la misma pregunta
                  —«¿dónde y cuándo?»— y separarlos haría de cada logro un
                  párrafo de tres líneas.

                  EL AÑO SALE DEL SERVIDOR YA CALCULADO. Cortarlo acá de la
                  fecha le cambiaría el año a un torneo del 31 de diciembre
                  según dónde esté quien mira.
                */}
                {/*
                  EL AÑO VA SIEMPRE Y EL CLUB SOLO SI HAY. Un reto amistoso no
                  tiene club —no cuenta en ningún ranking— y ahí el renglón es
                  el año solo. Dibujarlo condicionado a que haya club dejaría
                  sin año justamente a las filas que ya no dicen dónde fue.
                */}
                <span className="help achievement-where">
                  {logro.kind === 'YEAR_END_RANK'
                    ? t('achievements.yearEndOf', { year: logro.year })
                    : logro.year}
                  {logro.communityName !== null && (
                    <> · {t('achievements.atClub', { club: logro.communityName })}</>
                  )}
                </span>
              </span>
              {/* LA CATEGORÍA SIEMPRE, con su división al lado. Un campeón de
                  C2 y uno de OPEN ganaron cosas distintas, y sin decirlo los
                  dos se leen igual. El escudo lo dice de un vistazo; la letra
                  lo dice exacto. El icono NO repite la letra: la resume. */}
              <span className="help achievement-level">
                <DivisionIcon
                  division={divisionOf(logro.level)}
                  title={t(`division.${divisionOf(logro.level)}`)}
                  size={16}
                />
                {categoria}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="help">{t('achievements.expire')}</p>
    </section>
  );
}
